import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactsService } from 'src/app/core/lists';
import { StringUtilsService } from '../../../../../../../../core/services/string-utils.service';
import { LayoutUtilsService, MessageType } from '../../../../../../../../core/_base/crud';
import { ConfirmedValidator } from '../reset-contact-credential/reset-contact-credential.component';
import { ContactApplicationModel } from '../../../../../../../../core/lists/_models/contact-application.model';
import { errorMessages } from '../../../../enums/errors-messages.enum';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-contact-credential',
  templateUrl: './contact-credential.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactCredentialComponent implements OnInit, OnDestroy {
  createPasswordForm: FormGroup;
  private subscriptions: any[] = [];
  applications: ContactApplicationModel[] = [];
  result: any;
  @Input() contact: { id: any; cell_phone: any; username?: string };
  @Input() isViewMode: any;
  hasFormErrors = false;
  error = 'Oh snap! Change a few things up and try submitting again.';
  @Output() createdCred = new EventEmitter<any>();
  loading = true;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    public stringUtils: StringUtilsService,
    private contactService: ContactsService,
    private layoutUtilsService: LayoutUtilsService,
  ) {}

  ngOnInit() {
    const username = typeof this.contact?.username === 'string' ? this.contact.username.trim() : '';
    if (!this.contact?.id || !username) {
      this.initialCreatePasswordForm();
      return;
    }
    this.subscriptions.push(
      this.contactService.getLoginHistory(this.contact.id).subscribe((res) => {
        this.result = res;
        this.result?.applications?.forEach((a: ContactApplicationModel) =>
          this.applications.push(a),
        );
        this.loading = false;
        this.cdr.detectChanges();
      }),
    );
    setTimeout(() => {
      if (!this.result) {
        this.initialCreatePasswordForm();
      }
    }, 1500);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
  }

  checkValue(event: any, applicationId: number) {
    this.applications[applicationId - 1].enabled = event;
    if (!this.applications[applicationId - 1].enabled) {
      this.applications[applicationId - 1].permissions?.forEach((x) => {
        x.enabled = false;
      });
    }
  }

  changeEnable(event: any, applicationId: number, permissionId: number) {
    this.applications[applicationId - 1].permissions[permissionId - 1].enabled = event;
    if (this.applications[applicationId - 1].permissions[permissionId - 1].enabled) {
      this.applications[applicationId - 1].enabled = true;
    }
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.createPasswordForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  initialCreatePasswordForm() {
    this.createPasswordForm = this.fb.group(
      {
        username: [],
        firstField: [
          null,
          Validators.compose([
            Validators.required,
            Validators.pattern('[a-zA-z][a-zA-Z0-9._-]{3,}$'),
          ]),
        ],
        secField: [null, Validators.compose([Validators.required, Validators.minLength(6)])],
        password: [null],
        pin: [null],
        pass: [
          null,
          Validators.compose([
            Validators.required,
            Validators.pattern(
              '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&#^])[A-Za-z0-9$@$!%*?&#^]{8,}$',
            ),
          ]),
        ],
        rePassword: [null, Validators.required],
      },
      { validator: ConfirmedValidator('pass', 'rePassword') },
    );
    setTimeout(() => {
      const hideDiv = document.getElementById('hiddenUser');
      if (hideDiv) {
        hideDiv.style.display = 'none';
      }
    }, 600);
    if (this.isViewMode) {
      this.createPasswordForm.disable();
    }
    this.loading = false;
    this.cdr.detectChanges();
  }

  onSubmit() {
    this.hasFormErrors = false;
    const controls = this.createPasswordForm.controls;
    if (this.createPasswordForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      return;
    }
    const body = {
      password: this.createPasswordForm.controls.pass.value,
      password2: this.createPasswordForm.controls.pass.value,
      phone: this.contact.cell_phone,
      pin: this.createPasswordForm.controls.secField.value,
      username: this.createPasswordForm.controls.firstField.value,
    };
    for (const [key, value] of Object.entries(body)) {
      if (typeof value === 'string') {
        body[key] = value.trim();
      }
    }
    this.contactService.signupContact(body).subscribe(
      (res) => {
        if (res) {
          this.loadLoginHistory(this.contact.id);
          const message = 'Your credentials created successfully';
          this.layoutUtilsService.showActionNotification(
            message,
            MessageType.Read,
            6000,
            true,
            false,
          );
          this.createdCred.emit(true);
        }
        this.cdr.detectChanges();
      },
      (err) => {
        if (err.error.phone) {
          this.error = 'cell phone must not be empty';
          this.hasFormErrors = true;
          this.cdr.detectChanges();
        }
        if (err.error.pin) {
          this.error = err.error.pin;
          this.hasFormErrors = true;
          this.cdr.detectChanges();
        }
        if (err.error.cause) {
          this.error = 'Duplicate cell phone';
          this.hasFormErrors = true;
          this.cdr.detectChanges();
        }
        if (err.error?.message === errorMessages.username_exists) {
          this.createPasswordForm.controls.firstField.setErrors({ duplicatedName: true });
          this.createPasswordForm.controls.firstField.markAsTouched();
          this.cdr.detectChanges();
        }
        if (err.error.message === errorMessages.cellphone_exists) {
          this.error = errorMessages.cellphone_exists;
          this.hasFormErrors = true;
          this.cdr.detectChanges();
        }
        if (err.error.message === errorMessages.user_exists) {
          this.error = errorMessages.user_exists;
          this.hasFormErrors = true;
          this.cdr.detectChanges();
        }
      },
    );
    this.cdr.detectChanges();
  }

  updateApplications(item: any) {
    this.contactService.updateApplications(item, this.contact.id).subscribe(() => {
      this.layoutUtilsService.showActionNotification(
        'Applications Updated Successfully',
        MessageType.Update,
        3000,
        false,
        false,
      );
    });
  }

  loadLoginHistory(id: any) {
    this.contactService.getLoginHistory(id).subscribe((res) => {
      this.result = res;
      this.result?.applications?.forEach((x: ContactApplicationModel) => {
        this.applications[x.id - 1] = x;
      });
      this.cdr.detectChanges();
    });
  }

  getEditAppPermission() {
    const permissions = JSON.parse(localStorage.getItem('currentUserPermissions'));
    const canEditApplications = permissions.canEditApplications;
    return !canEditApplications;
  }

  onAlertClose() {
    this.hasFormErrors = false;
  }
}
